import React from 'react';
import { connect } from 'react-redux';
import { change, formValueSelector, reduxForm } from 'redux-form';
import { Button, Modal } from '@syneto/compass-react';
import FleetFirmwareVersions from '../../../../components/FleetFirmwareVersions';
import Form from '../../../../components/forms/form';
import {
	ADVISORY_FORM_NAME,
	ADVISORY_SEVERITIES,
	EMPTY_ADVISORY_FORM_VALUES,
	MATCH_OPS,
	MATCH_OP_LABELS
} from '../../../../constants/firmware-advisories';
import { advisoryToFormValues, componentTypeChangeFields } from '../../../../data/firmwareAdvisory/mapper';
import { findComponentTypeById } from '../../../../helpers/hardwareInventory';
import { useFleetFirmwareVersions } from '../../../../hooks/useFleetFirmwareVersions';
import { advisoryMatcherSummary } from './firmwareAdvisoryDisplay';
import { validateAdvisoryForm } from './firmwareAdvisoryValidation';

const selector = formValueSelector(ADVISORY_FORM_NAME);

const matchOpOptions = MATCH_OPS.map((op) => {
	return { label: MATCH_OP_LABELS[op], value: op };
});

const severityOptions = ADVISORY_SEVERITIES.map((severity) => {
	return { label: severity, value: severity };
});

let FirmwareAdvisoryFormModal = (props) => {
	const {
		advisory,
		componentTypes,
		componentTypesLoading,
		componentTypeId,
		affectedVersions,
		handleSubmit,
		submitting,
		onSubmitAdvisory,
		onCancel,
		dispatch
	} = props;

	const isEditing = Boolean(advisory);
	const selectedComponentType = findComponentTypeById(componentTypeId, componentTypes);
	const fleetVersions = useFleetFirmwareVersions(selectedComponentType);

	/*
	 * The catalog is an inner join on the fleet analytics table, so an advisory can legitimately
	 * point at a type that is not in the list. Without a synthetic option the picker would render
	 * blank and the next save would drop the id.
	 */
	const componentTypeOptions = (componentTypes ?? []).map((componentType) => {
		return { label: componentType.displayName, value: componentType.id };
	});

	if (isEditing && componentTypeId && !selectedComponentType) {
		componentTypeOptions.unshift({
			label: `${advisoryMatcherSummary(advisory) || componentTypeId} (not in the current catalog)`,
			value: componentTypeId
		});
	}

	/*
	 * `componentTypeId` here is the value this render was built from, which is still the previous one
	 * while the handler runs - the select dispatches its own change first and this closure does not
	 * see it. That is what makes it the right thing to compare the new pick against.
	 */
	const onComponentTypeChange = (value) => {
		const fields = componentTypeChangeFields(value, componentTypeId, componentTypes);

		if (!fields) {
			return;
		}

		Object.entries(fields).forEach(([field, fieldValue]) => {
			dispatch(change(ADVISORY_FORM_NAME, field, fieldValue));
		});
	};

	const onSelectVersion = (version) => {
		if ((affectedVersions ?? []).includes(version)) {
			return;
		}

		dispatch(change(ADVISORY_FORM_NAME, 'affectedVersions', [...(affectedVersions ?? []), version]));
	};

	/*
	 * The option labels carry the counts, but the stored value stays the bare version string - the
	 * API matches it by exact equality.
	 */
	const versionOptions = fleetVersions.versions.map((option) => {
		return {
			label: option.seenInScan
				? `${option.version} — ${option.componentCount} on ${option.applianceCount} appliances`
				: option.version,
			value: option.version
		};
	});

	return (
		<Modal show={true} size="lg" onHide={onCancel}>
			<Modal.Header>
				<Modal.Title>{isEditing ? 'Edit firmware advisory' : 'Add firmware advisory'}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<form noValidate>
					<fieldset>
						<Form.select
							label="Component type"
							name="componentTypeId"
							options={componentTypeOptions}
							onValueChange={onComponentTypeChange}
							emptyOption={componentTypesLoading ? 'Loading component types…' : 'Please select'}
							/*
							 * Changing the type on an existing advisory silently re-scopes it while the
							 * matcher below still shows the previous type's values.
							 */
							disabled={isEditing}
							required
						/>

						<p className="text-muted">
							Only component types with fleet analytics appear here.
						</p>

						<div className="row">
							<div className="col-4">
								<Form.select label="Manufacturer matches" name="manufacturerMatchOp" options={matchOpOptions} required />
							</div>
							<div className="col-8">
								<Form.text label="Manufacturer" name="manufacturerMatchValue" required />
							</div>
						</div>

						<div className="row">
							<div className="col-4">
								<Form.select label="Model matches" name="modelMatchOp" options={matchOpOptions} required />
							</div>
							<div className="col-8">
								<Form.text label="Model" name="modelMatchValue" required />
							</div>
						</div>

						<p className="text-muted">
							Values are matched against the normalized, lowercased manufacturer and model.
						</p>

						<FleetFirmwareVersions
							{...fleetVersions}
							selectedVersions={affectedVersions}
							onSelectVersion={onSelectVersion}
							manualEntryHint="Type a version to add it."
							unreportedNote={`${fleetVersions.unreportedCount} component`
								+ `${fleetVersions.unreportedCount === 1 ? '' : 's'} of this type report no firmware `
								+ `version, so no advisory can match ${fleetVersions.unreportedCount === 1 ? 'it' : 'them'}.`}
						/>

						<Form.creatableSelect
							label="Affected versions"
							name="affectedVersions"
							options={versionOptions}
							isLoading={fleetVersions.isLoading}
							emptyOption="Select or type the affected firmware versions"
							noOptionsMessage={() => {
								return selectedComponentType
									? 'No firmware versions reported for this component type. Type a version to add it.'
									: 'Pick a component type first.';
							}}
							loadingMessage={() => { return 'Loading firmware versions seen in the fleet…'; }}
							required
						/>

						<Form.text
							label="Target version"
							name="targetVersion"
							placeholder="The version appliances should upgrade to"
							required
						/>

						<Form.select label="Severity" name="severity" options={severityOptions} emptyOption="Please select" required />

						<Form.text label="Knowledge base URL" name="kbUrl" required />
						<Form.text label="Vendor release notes URL" name="vendorUrl" required />
					</fieldset>
				</form>
			</Modal.Body>

			<Modal.Footer>
				<Button role="secondary" onClick={onCancel}>Cancel</Button>
				<Button disabled={submitting} onClick={handleSubmit(onSubmitAdvisory)}>Save</Button>
			</Modal.Footer>
		</Modal>
	);
};

/*
 * keepDirtyOnReinitialize is deliberately off, unlike the maintenance type modals: with it on,
 * opening Edit on one advisory straight after another keeps the first one's edits on top of the
 * second one's values.
 */
FirmwareAdvisoryFormModal = reduxForm({
	form: ADVISORY_FORM_NAME,
	enableReinitialize: true,
	keepDirtyOnReinitialize: false,
	validate: validateAdvisoryForm,
	shouldValidate: () => { return true; }
})(FirmwareAdvisoryFormModal);

/*
 * `seed` fills in a create form, while `advisory` opens an edit form. A seed is deliberately a
 * separate prop: `isEditing` comes from `advisory`, so a partial advisory passed there to prefill the
 * form would turn it into an edit and lock the component type.
 */
export default connect((state, componentProps) => {
	return {
		initialValues: componentProps.advisory
			? advisoryToFormValues(componentProps.advisory)
			: { ...EMPTY_ADVISORY_FORM_VALUES, ...componentProps.seed },
		componentTypeId: selector(state, 'componentTypeId'),
		affectedVersions: selector(state, 'affectedVersions')
	};
})(FirmwareAdvisoryFormModal);
